import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, filter, map, of, take, timeout } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { AutoLogoutService } from './autoLogout.service';

/**
 * Keeps the global spinner overlay visible while session/cache restore and post-refresh
 * routing complete. While active, the auth interceptor does not toggle loading off.
 */
@Injectable({
  providedIn: 'root',
})
export class SessionBootstrapService {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly autoLogoutService = inject(AutoLogoutService);
  private active = false;
  private startupCompleted = false;

  isActive(): boolean {
    return this.active;
  }

  shouldHideRouterOutlet(): boolean {
    return this.active;
  }

  beginRestore(): void {
    this.active = true;
    this.authService.pinLoading();
  }

  /** Keep loader until navigation settles on a workflow route (not login / auth pages). */
  finishAfterWorkflowNavigation(): void {
    if (!this.active || this.startupCompleted) {
      return;
    }

    const current = this.normalizePath(this.router.url);
    if (this.isWorkflowRoute(current)) {
      this.completeStartup(0);
      return;
    }

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map((event) => this.normalizePath(event.urlAfterRedirects)),
        filter((path) => this.isWorkflowRoute(path)),
        take(1),
        timeout({ first: 20000 }),
        catchError(() => of(null))
      )
      .subscribe(() => this.completeStartup(0));
  }

  isAuthRoute(path: string): boolean {
    return !this.isWorkflowRoute(path);
  }

  isWorkflowRoute(path: string): boolean {
    const normalized = this.normalizePath(path);
    const authRoutes = new Set([
      '/login',
      '/',
      '/forgot-password',
      '/reset-password',
      '/create-password',
    ]);
    return !authRoutes.has(normalized);
  }

  getBrowserPath(): string {
    if (typeof window === 'undefined') {
      return this.normalizePath(this.router.url);
    }
    return this.normalizePath(window.location.pathname);
  }

  resolveIntendedRoute(): string {
    if (typeof window !== 'undefined') {
      const browserPath = this.normalizePath(window.location.pathname);
      if (this.isWorkflowRoute(browserPath)) {
        return browserPath;
      }
    }

    const lastVisited = this.authService.getLastVisitedRoute();
    if (lastVisited && this.isWorkflowRoute(lastVisited)) {
      return lastVisited;
    }

    const role = Number(this.authService.getUserDetailsFromStorage()?.userRole);
    return role === 3 ? '/system-admin' : '/quote';
  }

  completeStartup(minDisplayMs = 0): void {
    if (this.startupCompleted) {
      return;
    }
    this.startupCompleted = true;

    const finish = () => {
      this.active = false;
      this.authService.unpinLoading();
      if (
        this.authService.isAuthenticated() &&
        this.authService.getIsOTPVerified()
      ) {
        this.autoLogoutService.setupAutoLogout();
      }
    };

    if (minDisplayMs > 0) {
      setTimeout(finish, minDisplayMs);
      return;
    }

    requestAnimationFrame(() => requestAnimationFrame(finish));
  }

  private normalizePath(url: string): string {
    const path = url.split('?')[0]?.split('#')[0] ?? '/';
    if (path.length > 1 && path.endsWith('/')) {
      return path.slice(0, -1);
    }
    return path || '/';
  }
}
